import { PersonDuplicates } from '@fs/zion-icon'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@fs/zion-ui'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useHistoricalList } from './historicalList'
import { buildTreeDerivedEditRows } from './treeDerivedFieldRows'
import useNotifications from '../../../../hooks/useNotifications'
import { createPerson, prePopulateHistoricalPerson } from '../../../../services/famousHistoricalService'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'

const defaultPersonObject = {
  birthDateItem: { key: 'birthDateItem', primaryText: '' },
}

export default function HistoricalCreatePersonDialog({ countries, ...overlay }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [person, setPerson] = useState(defaultPersonObject)
  const [country, setCountry] = useState()
  const [alternatePerson, setAlternatePerson] = useState()
  // DATA-12381: with the flag on, the admin supplies a Person ID and Family Tree supplies the rest.
  // With it off every behavior below is exactly as it was, including the Alternate Name button (BR-34).
  const { metaDataId } = useHistoricalList()
  const treeDerivedFieldsFlag = useFeatureFlag('tree_admin_dmcsonsole_historical')
  // .isOn, not the object itself -- the hook returns a flag object, which is always truthy.
  const treeDerivedFields = treeDerivedFieldsFlag.isOn
  const [prePopulateError, setPrePopulateError] = useState()
  const data = getHistoricalDataForm()
  const givenNameTextBoxRef = useRef()
  const pidTextBoxRef = useRef()

  useEffect(() => {
    if (overlay.isOpen && !overlay.transitioning) {
      // With the flag on, Given Name is no longer an input, so its ref is never attached -- focus the
      // Person ID field, which is now the first thing an admin fills in.
      const focusTarget = treeDerivedFields ? pidTextBoxRef.current : givenNameTextBoxRef.current
      focusTarget?.focus()
    }
  }, [overlay.isOpen, overlay.transitioning, treeDerivedFields])

  async function prePopulateFromPid() {
    setPrePopulateError()
    try {
      const { pid, fields } = await prePopulateHistoricalPerson(person.pid.trim())
      // The form keeps its birth date in birthDateItem, which is what submit reads -- a derived
      // birthDate left on its own would be overwritten by an empty picker on the way out.
      const birthDateItem = fields.birthDate
        ? { key: 'birthDateItem', primaryText: fields.birthDate, formattedDate: fields.birthDate }
        : person.birthDateItem
      setPerson({ ...person, ...fields, pid, birthDateItem })
    } catch (e) {
      // 404 and 400 are the person: the summary endpoint answers an unknown PID with one or the other.
      // Anything else is the service, and saying "not found in Family Tree" for that sends the admin
      // hunting for a person who is plainly there.
      const status = e?.response?.status
      const message =
        !status || status === 404 || status === 400
          ? t('tools.lists.pidDidNotResolve', 'That Person ID could not be found in Family Tree.')
          : t('tools.lists.prePopulateUnavailable', 'Could not reach Family Tree to fill in this person.')
      const detail = status ? `HTTP ${status}` : e?.message
      setPrePopulateError(detail ? `${message} (${detail})` : message)
    }
  }

  function shouldDisableSubmitButtons() {
    if (alternatePerson) {
      if (JSON.stringify(alternatePerson) === JSON.stringify(person)) {
        return true
      }
    }
    // U10: no PID requirement. The new list matches by description, so a record can be typed from
    // scratch, and a PID that did not resolve no longer makes the record inert -- it only means Family
    // Tree could not fill the fields in, which the admin can do by hand. BR-31 assumed the PID was the
    // record's identity; on this list it is a convenience.
    return Object.keys(person).length === 1 && !person.birthDateItem?.formattedDate
  }

  const submitBtnOnClickHandler = async (isSubmitBtn) => {
    const personWithFormattedDates = {
      ...person,
      birthDate: person.birthDateItem && person.birthDateItem.formattedDate,
    }
    delete personWithFormattedDates.birthDateItem

    const personDto = {
      metaDataId,
      personJSON: JSON.stringify(personWithFormattedDates),
      matchType: 'HISTORICAL',
      country,
    }
    try {
      await createPerson(personDto)
      if (isSubmitBtn) {
        setPerson(defaultPersonObject)
        setAlternatePerson()
      } else {
        setAlternatePerson(JSON.parse(JSON.stringify(person)))
      }
      givenNameTextBoxRef.current.select()
    } catch (err) {
      error(`Error creating historical person: ${err.message || ''}`, err)
    }
  }

  const cancelBtnOnClickHandler = () => {
    setPerson(defaultPersonObject)
    setCountry()
    overlay.close()
  }

  const onPersonChange = (fieldType, newValue) => {
    const newPerson = { ...person, [fieldType]: newValue }
    if (newValue === '') {
      delete newPerson[fieldType]
    }
    setPerson(newPerson)
  }

  const onPidChange = (value) => {
    // A stale error beside a PID the admin has since corrected reads as a refusal to save.
    setPrePopulateError(undefined)
    onPersonChange('pid', value)
  }

  function getHistoricalDataForm() {
    return [
      {
        key: 'historicalGivenName',
        label: <b>{t('givenName', 'Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.givenName}
            onChange={(e) => onPersonChange('givenName', e.target.value)}
            ref={(element) => {
              givenNameTextBoxRef.current = element
            }}
          />
        ),
      },
      {
        key: 'historicalSurname',
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: (
          <TextField type="text" value={person.surname} onChange={(e) => onPersonChange('surname', e.target.value)} />
        ),
      },
      {
        key: 'historicalPersonId',
        label: <b>{t('personId', 'Person ID')}</b>,
        detail: (
          <>
            <TextField
              type="text"
              // The label sits in its own cell, so the input has no accessible name of its own. Naming it
              // also stops tests having to find it by position, which moved when U10 made the names
              // editable again and put two text fields in front of it.
              aria-label={t('personId', 'Person ID')}
              value={person.pid}
              onChange={(e) => onPidChange(e.target.value)}
              ref={(element) => {
                pidTextBoxRef.current = element
              }}
            />
            {treeDerivedFields && (
              <>
                <Button emphasis="medium" onClick={prePopulateFromPid} disabled={!person.pid}>
                  {t('tools.lists.prePopulate', 'Pre-populate')}
                </Button>
                {prePopulateError && <span role="alert">{prePopulateError}</span>}
              </>
            )}
          </>
        ),
      },
      {
        key: 'historicalLabel',
        label: <b>{t('label', 'Label')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectLabel', 'Select Label')}
            value={person.label}
            onChange={(e) => onPersonChange('label', e.target.value)}
            options={[
              {
                value: 'Historical',
                label: t('historical', 'Historical'),
              },
              {
                value: 'Church Leader',
                label: t('churchLeader', 'Church Leader'),
              },
            ]}
          />
        ),
      },
      {
        key: 'historicalWhyFamous',
        label: <b>{t('tools.lists.whyFamous', 'Why Famous')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.reasonWhyFamous}
            onChange={(e) => onPersonChange('reasonWhyFamous', e.target.value)}
          />
        ),
      },
      {
        key: 'historicalBirthDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('tools.lists.labels.birthDate', 'Birth Date')}
            selectedItem={person.birthDateItem}
            setSelectedItem={(e) => onPersonChange('birthDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      ...(treeDerivedFields
        ? buildTreeDerivedEditRows({
            t,
            values: person,
            onChange: (field, value) => onPersonChange(field, value),
            keyPrefix: 'historical',
            radioNamePrefix: 'historical',
          })
        : []),
      // DATA-12381: Needs Sources, Locked and Possible Duplicates left the historical admin workflow, so
      // the flag hides them. Nothing is defaulted in their place -- the radios already start unchecked,
      // so an untouched form has always submitted personJSON without these keys.
      ...(treeDerivedFields
        ? []
        : [
            {
              key: 'historicalNeedsSources',
              label: <b>{t('tools.lists.labels.needsSources', 'Needs Sources')}</b>,
              detail: (
                <RadioGroup orientation="horizontal">
                  <Radio
                    name="historicalNeedsSourcesRadioNo"
                    checked={person.needsSources === true}
                    label={t('yes', 'Yes')}
                    value="true"
                    onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
                  />
                  <Radio
                    name="historicalNeedsSourcesRadioNo"
                    checked={person.needsSources === false}
                    label={t('no', 'No')}
                    value="false"
                    onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
                  />
                </RadioGroup>
              ),
            },
            {
              key: 'historicalLocked',
              label: <b>{t('accessControl.locked', 'Locked')}</b>,
              detail: (
                <RadioGroup orientation="horizontal">
                  <Radio
                    name="historicalLockedRadioYes"
                    checked={person.locked === true}
                    label={t('yes', 'Yes')}
                    value="true"
                    onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
                  />
                  <Radio
                    name="historicalLockedRadioNo"
                    checked={person.locked === false}
                    label={t('no', 'No')}
                    value="false"
                    onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
                  />
                </RadioGroup>
              ),
            },
            {
              key: 'historicalPossibleDuplicates',
              label: <b>{t('tools.lists.labels.possibleDuplicates', 'Possible Duplicates')}</b>,
              detail: (
                <TextField
                  type="text"
                  value={person?.possibleDuplicates?.replaceAll('"', '')}
                  onChange={(e) => onPersonChange('possibleDuplicates', e.target.value)}
                />
              ),
            },
          ]),
    ]
  }

  return (
    <DialogOverlay
      {...overlay}
      title={t('tools.lists.headings.createNewHistoricalPerson', 'Create New Historical Person')}
      size="xxl"
    >
      <Grid>
        <Cell columns={3}>
          <Select
            id="country"
            placeholder={t('tools.lists.placeholders.selectMultiAreaTeam', 'Select Multi-area Team')}
            label={t('tools.lists.labels.multiAreaTeam', 'Multi-area Team')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            options={countries}
          />
        </Cell>
        <p columns={9} />
      </Grid>
      <DmcDataView data={data} />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={() => submitBtnOnClickHandler(true)} disabled={shouldDisableSubmitButtons()}>
            {t('action.submit', 'Submit')}
          </Button>
          {/* U10: back on both lists. It was hidden while one record per PID was the rule; several
              records may now share one, which is how "Abe" becomes findable alongside "Abraham". */}
          <Button
            Icon={PersonDuplicates}
            emphasis="high"
            onClick={() => submitBtnOnClickHandler(false)}
            disabled={shouldDisableSubmitButtons()}
          >
            {t('name.alternate', 'Alternate Name')}
          </Button>
          <Button emphasis="medium" onClick={cancelBtnOnClickHandler}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
